import { Group, Paper, Portal, ScrollArea, Select, SimpleGrid, Stack, Text } from '@mantine/core';
import { FC } from 'react';
import { useMap } from 'react-map-gl';

import classes from './assignment-map-legend-panel.module.css';

import type { GpAssignmentMapPoint, SamplingPeriod } from '../../data-access/types/gp-sampling-assignment';

import {
  AssignmentMapColorMode,
  buildPeriodSelectOptions,
  legendModeTitleKey,
  ORGANIC_LEGEND_ITEMS,
  SAMPLING_LEGEND_ITEMS,
  SAMPLING_PERIODS,
} from './assignment-map-constants';
import {
  buildLaboratoryLegendItems,
  buildSamplerLegendItems,
  LegendSwatch,
} from './assignment-map-legend-shared';
import {
  GP_PERIOD_FILL_COLORS,
  GP_PERIOD_UNKNOWN_COLOR,
  NGP_PERIOD_LEGEND_ITEMS,
  SAMPLER_UNASSIGNED_COLOR,
} from './point-colors';

interface AssignmentMapLegendPanelProps {
  colorMode: AssignmentMapColorMode;
  i18n: (key: string, options?: Record<string, unknown>) => string;
  isNgp: boolean;
  legendPeriod: SamplingPeriod;
  onLegendPeriodChange: (period: SamplingPeriod) => void;
  points: GpAssignmentMapPoint[];
  samplerLegendMode?: 'organization' | 'user';
  variant?: 'sidebar' | 'overlay';
}

function getMapBottomLeftPortalTarget(mapId: string) {
  return document
    .getElementById(mapId)
    ?.getElementsByClassName('mapboxgl-ctrl-bottom-left')[0] as HTMLElement | undefined;
}

export const AssignmentMapLegendPanel: FC<AssignmentMapLegendPanelProps> = ({
  colorMode,
  i18n,
  isNgp,
  legendPeriod,
  onLegendPeriodChange,
  points,
  samplerLegendMode = 'user',
  variant = 'sidebar',
}) => {
  const map = useMap();
  const mapId = map.current?.getMap().getContainer().id;
  const isOverlay = variant === 'overlay';
  const samplerLegend = buildSamplerLegendItems(points, samplerLegendMode);
  const laboratoryLegend = buildLaboratoryLegendItems(points);
  const periodOptions = buildPeriodSelectOptions(i18n);

  const content = (
    <Stack gap="sm">
      <Text fw={600} size={isOverlay ? 'xs' : 'sm'}>
        {i18n(legendModeTitleKey(colorMode))}
      </Text>

      {colorMode === 'sampler' ? (
        <ScrollArea.Autosize mah={isOverlay ? 160 : 220} offsetScrollbars type="hover">
            <Stack gap={6}>
              {samplerLegend.items.map((item) => (
                <Group gap={6} key={item.id} wrap="nowrap">
                  <LegendSwatch fill={item.color} />
                  <Text lineClamp={1} size="xs">
                    {item.label}
                  </Text>
                </Group>
              ))}
              {samplerLegend.hasUnassigned ? (
                <Group gap={6} wrap="nowrap">
                  <LegendSwatch fill={SAMPLER_UNASSIGNED_COLOR} />
                  <Text size="xs">{i18n('legendSamplerUnassigned')}</Text>
                </Group>
              ) : null}
              {!samplerLegend.items.length && !samplerLegend.hasUnassigned ? (
                <Text c="dimmed" size="xs">
                  {i18n('legendNoData')}
                </Text>
              ) : null}
            </Stack>
          </ScrollArea.Autosize>
        ) : null}

        {colorMode === 'laboratory' ? (
          <ScrollArea.Autosize mah={isOverlay ? 160 : 220} offsetScrollbars type="hover">
            <Stack gap={6}>
              {laboratoryLegend.items.map((item) => (
                <Group gap={6} key={item.id} wrap="nowrap">
                  <LegendSwatch fill={item.color} />
                  <Text lineClamp={1} size="xs">
                    {item.label}
                  </Text>
                </Group>
              ))}
              {laboratoryLegend.hasUnassigned ? (
                <Group gap={6} wrap="nowrap">
                  <LegendSwatch fill={SAMPLER_UNASSIGNED_COLOR} />
                  <Text size="xs">{i18n('legendLaboratoryUnassigned')}</Text>
                </Group>
              ) : null}
              {!laboratoryLegend.items.length && !laboratoryLegend.hasUnassigned ? (
                <Text c="dimmed" size="xs">
                  {i18n('legendNoData')}
                </Text>
              ) : null}
            </Stack>
          </ScrollArea.Autosize>
        ) : null}

        {colorMode === 'organic' ? (
          <SimpleGrid cols={isOverlay ? 1 : 2} spacing={6} verticalSpacing={6}>
            {ORGANIC_LEGEND_ITEMS.map((item) => (
              <Group gap={6} key={item.labelKey} wrap="nowrap">
                <LegendSwatch fill={item.color} />
                <Text lineClamp={2} size="xs">
                  {i18n(item.labelKey)}
                </Text>
              </Group>
            ))}
          </SimpleGrid>
        ) : null}

        {colorMode === 'period' ? (
          <Stack gap="sm">
            <Select
              data={periodOptions}
              label={i18n('legendPeriodView')}
              onChange={(value) => onLegendPeriodChange((value as SamplingPeriod) ?? 'spring')}
              size="xs"
              value={legendPeriod}
            />
            {isNgp ? (
              <Stack gap={6}>
                {NGP_PERIOD_LEGEND_ITEMS.map((item) => (
                  <Group gap={6} key={item.labelKey} wrap="nowrap">
                    <LegendSwatch fill="#868e96" stroke={item.color} strokeWidth={3} />
                    <Text size="xs">{i18n(item.labelKey)}</Text>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Stack gap={6}>
                {SAMPLING_PERIODS.map((period) => (
                  <Group gap={6} key={period} wrap="nowrap">
                    <LegendSwatch fill={GP_PERIOD_FILL_COLORS[period]} />
                    <Text size="xs">
                      {periodOptions.find((option) => option.value === period)?.label ?? period}
                    </Text>
                  </Group>
                ))}
                <Group gap={6} wrap="nowrap">
                  <LegendSwatch fill={GP_PERIOD_UNKNOWN_COLOR} />
                  <Text size="xs">{i18n('legendPeriodUnknown')}</Text>
                </Group>
              </Stack>
            )}
          </Stack>
        ) : null}

        {colorMode === 'sampling' ? (
          <SimpleGrid cols={isOverlay ? 1 : 2} spacing={6} verticalSpacing={6}>
            {SAMPLING_LEGEND_ITEMS.map((item) => (
              <Group gap={6} key={item.labelKey} wrap="nowrap">
                <LegendSwatch fill={item.color} />
                <Text lineClamp={2} size="xs">
                  {i18n(item.labelKey)}
                </Text>
              </Group>
            ))}
          </SimpleGrid>
        ) : null}
    </Stack>
  );

  if (isOverlay) {
    if (!mapId) {
      return null;
    }

    return (
      <Portal target={getMapBottomLeftPortalTarget(mapId)}>
        <div className={`${classes.overlay} mapboxgl-ctrl`}>{content}</div>
      </Portal>
    );
  }

  return (
    <Paper p="sm" radius="sm" withBorder>
      {content}
    </Paper>
  );
};
